import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { CodeConfirmationForm } from '@/ui/components/CodeConfirmationForm/CodeConfirmationForm';
import { PageLoader } from '@/layouts/components/PageLoader/PageLoader';
import { key } from '@/assets/images/emojis';
import { formatPhoneForDisplay } from '@/lib/phoneUtils';
import { routes } from '@/routes/routes';
import { useError } from '@/providers/Error';
import { genericMessages, phoneOtpMessages } from '@/constants/errorMessages';
import { useBrokerOtp } from '../../hooks';
import styles from './BrokerOtpPage.module.scss';

const OTP_LENGTH = 6;
const RESEND_DELAY = 120;

export function BrokerOtpPage() {
  const { offerId } = useParams<{ offerId: string }>();
  const navigate = useNavigate();
  const { error, setError } = useError();
  const [code, setCode] = useState('');

  const {
    applicant,
    isLoadingApplicant,
    requestOtp,
    confirmOtp,
    isRequestingOtp,
    isConfirmingOtp,
  } = useBrokerOtp(offerId);

  if (!offerId) {
    return <Navigate to={routes.public.signup} replace />;
  }

  if (isLoadingApplicant) {
    return <PageLoader />;
  }

  const phone = applicant?.phone ?? '';
  const isLoading = isRequestingOtp || isConfirmingOtp;

  const handleResendCode = async () => {
    try {
      const response = await requestOtp();
      if (!response.isSuccess) {
        setError(
          phoneOtpMessages[response.error as keyof typeof phoneOtpMessages] ??
            genericMessages.unexpected
        );
        return;
      }
      setCode('');
    } catch {
      setError(genericMessages.unexpected);
    }
  };

  const handleCodeChange = async (value: string) => {
    setCode(value);

    if (value.length === OTP_LENGTH) {
      try {
        const response = await confirmOtp({ phone, code: value });
        if (!response.isSuccess) {
          setError(
            phoneOtpMessages[response.error as keyof typeof phoneOtpMessages] ??
              genericMessages.unexpected
          );
          return;
        }
        navigate(routes.wizard.base);
      } catch {
        setError(genericMessages.unexpected);
      }
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <img src={key} alt="" className={styles.emoji} />
        <h1 className={styles.title}>Introdu codul de confirmare</h1>
        <p className={styles.subtitle}>
          Confirmă codul trimis prin SMS către {formatPhoneForDisplay(phone)} (
          <Link
            to={routes.brokerOffer.confirmPhone(offerId)}
            className={styles.changeNumberLink}
          >
            Schimbă numărul
          </Link>
          )
        </p>
      </div>

      <CodeConfirmationForm
        value={code}
        className={styles.codeForm}
        onChange={handleCodeChange}
        onResendCode={handleResendCode}
        isLoading={isLoading}
        length={OTP_LENGTH}
        resendDelay={RESEND_DELAY}
        error={error}
        autoFocus
      />
    </div>
  );
}
